import { TimelineElement, TimelineDetails } from "../model/notification";
import { codiciStatusTimeline } from "../model/notification";

const CATEGORIE_ESCLUSE = new Set([
    "VALIDATE_NORMALIZE_ADDRESSES_REQUEST",
    "SENDER_ACK_CREATION_REQUEST",
    "AAR_CREATION_REQUEST",
    "AAR_GENERATION",
    "PUBLIC_REGISTRY_CALL",
    "PUBLIC_REGISTRY_RESPONSE",
    "PROBABLE_SCHEDULING_ANALOG_DATE",
    "SCHEDULE_ANALOG_WORKFLOW",
    "SCHEDULE_REFINEMENT",
    "NOTIFICATION_COST_VALIDATION_REQUEST",
    "NOTIFICATION_COST_VALIDATION_RESPONSE",
    "NOTIFICATION_CANCELLATION_REQUEST",
    "NOTIFICATION_CANCELLED_DOCUMENT_CREATION_REQUEST",
    "NOTIFICATION_VIEWED_CREATION_REQUEST",
    "COMPLETELY_UNREACHABLE_CREATION_REQUEST",
    "PREPARE_ANALOG_DOMICILE_FAILURE",
    "GENERATED_F24",
    "DIGITAL_SUCCESS_WORKFLOW",
    "DIGITAL_DELIVERY_CREATION_REQUEST",
    "SEND_DIGITAL_DOMICILE",
    "NOTIFICATION_TIMELINE_REWORKED",
]);

const TRADUZIONI_CATEGORIA: Record<string, string> = {
    "REQUEST_ACCEPTED": "Notifica presa in carico dalla piattaforma",
    "NORMALIZED_ADDRESS": "Indirizzo del destinatario normalizzato",
    "GET_ADDRESS": "Ricerca indirizzo",
    "SEND_COURTESY_MESSAGE": "Invio messaggio di cortesia",
    "PREPARE_ANALOG_DOMICILE": "Preparazione invio raccomandata",
    "SEND_ANALOG_DOMICILE": "Invio raccomandata analogica",
    "SEND_ANALOG_PROGRESS": "Aggiornamento stato raccomandata",
    "SEND_ANALOG_FEEDBACK": "Esito finale raccomandata",
    "ANALOG_SUCCESS_WORKFLOW": "Raccomandata consegnata con successo",
    "ANALOG_FAILURE_WORKFLOW": "Raccomandata non consegnata",
    "SEND_DIGITAL": "Invio notifica digitale (PEC/SERCQ)",
    "SEND_DIGITAL_FEEDBACK": "Esito invio digitale",
    "DIGITAL_PROG": "Avanzamento invio digitale",
    "SEND_DIGITAL_PROGRESS": "Avanzamento invio digitale",
    "REFINEMENT": "Perfezionamento notifica per decorrenza termini",
    "SCHEDULE_REFINEMENT": "Pianificazione perfezionamento",
    "COMPLETELY_UNREACHABLE": "Destinatario completamente irreperibile",
    "NOTIFICATION_CANCELLED": "Notifica annullata",
    "VALIDATED_F24": "F24 validato",
    "VALIDATE_F24_REQUEST": "Richiesta validazione F24",
    "GENERATE_F24_REQUEST": "Richiesta generazione F24",
    "REQUEST_REFUSED": "Notifica rifiutata",
    "NOTIFICATION_VIEWED": "Notifica visualizzata dal destinatario",
};

const TRADUZIONI_SOURCE: Record<string, string> = {
    "PLATFORM": "piattaforma",
    "SPECIAL": "domicilio speciale",
    "GENERAL": "registro generale",
};

const TRADUZIONI_DIGITAL_TYPE: Record<string, string> = {
    "PEC": "PEC",
    "SERCQ": "SERCQ",
    "APPIO": "App IO",
    "TPP": "App di terze parti",
    "EMAIL": "Email",
    "SMS": "SMS",
};

function formatTimestamp(ts: string): { date: string; time: string } {
    const dateObj = new Date(ts);

    const date = dateObj.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    });

    const time = dateObj.toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit"
    });

    return { date, time };
}

const getGeneralRegistryName = (details: TimelineDetails): string => {
    if (details.physicalAddress) {
        return "registro generale (ANPR)";
    }
    return "registro generale (INAD)";
};

const describeGetAddress = (details: TimelineDetails): string => {
    let source = TRADUZIONI_SOURCE[details.digitalAddressSource ?? ""] ?? details.digitalAddressSource;

    if (details.digitalAddressSource === "GENERAL") {
        source = getGeneralRegistryName(details);
    }

    const disponibile = details.isAvailable ? "trovato" : "non trovato";
    const tipoIndirizzo = details.physicalAddress ? "fisico" : "digitale";

    return `Indirizzo ${tipoIndirizzo} ${disponibile} - fonte: ${source}`;
};

const describeCourtesyMessage = (details: TimelineDetails): string => {
    const tipo = TRADUZIONI_DIGITAL_TYPE[details.digitalAddress?.type ?? ""] ?? details.digitalAddress?.type;
    return `Messaggio di cortesia inviato via ${tipo}`;
};

const describeAnalogAddress = (details: TimelineDetails): string => {
    const addr = details.physicalAddress;
    return addr
        ? `${addr.address ?? ""} ${addr.zip ?? ""} ${addr.municipality ?? ""} (${addr.province ?? ""})`
        : "indirizzo non disponibile";
};

const describeAnalogDomicile = (details: TimelineDetails): any => {
    const tentativo = (details.sentAttemptMade ?? 0) + 1;
    return `Tentativo ${tentativo} - ${describeAnalogAddress(details)}`;
};

const describeAnalogProgress = (details: TimelineDetails): string => {
    const code = details.deliveryDetailCode ?? "";
    const traduzione = codiciStatusTimeline[code] ?? code;
    const raccomandata = details.registeredLetterCode
        ? ` - Codice raccomandata: ${details.registeredLetterCode}`
        : "";
    return `${code} - ${traduzione}${raccomandata}`;
};

const describeAnalogFeedback = (details: TimelineDetails): string => {
    const esito = details.responseStatus === "OK" ? "Consegnata" : "Non consegnata";
    const code = details.deliveryDetailCode ?? "";
    const traduzione = codiciStatusTimeline[code] ? ` (${codiciStatusTimeline[code]})` : "";
    const causa = details.deliveryFailureCause
        ? ` - Causa: ${codiciStatusTimeline[details.deliveryFailureCause] ?? details.deliveryFailureCause}`
        : "";
    return `Esito: ${esito}${traduzione}${causa}`;
};

const describeAnalogSuccess = (details: TimelineDetails): string => {
    const addr = details.physicalAddress;
    const indirizzo = addr ? `${addr.address ?? ""} ${addr.municipality ?? ""}` : "";
    return indirizzo ? `Consegnata con successo a ${indirizzo}` : '';
};

const describeDigital = (details: TimelineDetails): string => {
    const tipo = TRADUZIONI_DIGITAL_TYPE[details.digitalAddress?.type ?? ""] ?? details.digitalAddress?.type ?? "";
    const indirizzo = details.digitalAddress?.address ?? "";
    return indirizzo ? `${tipo} ${indirizzo}` : "";
};

const describeDigitalFeedback = (details: TimelineDetails): string => {
    const esito = details.responseStatus === "OK" ? "Consegnato" : "Non consegnato";
    const code = details.deliveryDetailCode ?? "";
    const traduzione = codiciStatusTimeline[code] ? ` (${codiciStatusTimeline[code]})` : "";
    return `Esito digitale: ${esito}${traduzione}`;
};

const describeNotificationViewed = (details: TimelineDetails): string => {
    const internalId = details.delegateInfo?.internalId;
    return internalId ? `(Codice Univoco: ${internalId})` : "";
};

const describeRefused = (details: TimelineDetails): string => {
    if (details.refusalReasons?.[0]?.detail.includes("address is not valid")) {
        return "Indirizzo non valido, non è possibile normalizzare l'indirizzo del destinatario";
    }
    return "Richiesta rifiutata";
};


const DESCRITTORI: Partial<Record<string, (details: TimelineDetails) => string>> = {
    "GET_ADDRESS": describeGetAddress,
    "SEND_COURTESY_MESSAGE": describeCourtesyMessage,
    "PREPARE_ANALOG_DOMICILE": describeAnalogDomicile,
    "SEND_ANALOG_DOMICILE": describeAnalogDomicile,
    "SEND_ANALOG_PROGRESS": describeAnalogProgress,
    "SEND_ANALOG_FEEDBACK": describeAnalogFeedback,
    "ANALOG_SUCCESS_WORKFLOW": describeAnalogSuccess,
    "SEND_DIGITAL": describeDigital,
    "SEND_DIGITAL_PROGRESS": describeDigital,
    "DIGITAL_PROG": describeDigital,
    "REQUEST_REFUSED": describeRefused,
    "SEND_DIGITAL_FEEDBACK": describeDigitalFeedback,
    "NOTIFICATION_VIEWED": describeNotificationViewed,
    "NORMALIZED_ADDRESS": () => `Indirizzo normalizzato per destinatario`,
    "REFINEMENT": () => `Notifica perfezionata per decorrenza termini`,
};

const getEventDescription = (el: TimelineElement): { descrizione: string; fromDescrittore: boolean } => {
    const descrittore = DESCRITTORI[el.category];
    const traduzione = TRADUZIONI_CATEGORIA[el.category];
    if (descrittore) {
        return { descrizione: descrittore(el.details), fromDescrittore: true };
    } else if (traduzione) {
        return { descrizione: traduzione, fromDescrittore: false };
    }
    return { descrizione: "EVENTO NON SUPPORTATO", fromDescrittore: false };
};

export function buildTimelineText(timeline: Array<TimelineElement>): string {
    const lines: Array<string> = [];

    const seen = new Set<string>();
    const filtered = timeline.filter((el) => {
        if (CATEGORIE_ESCLUSE.has(el.category)) { return false; }
        const key = `${el.category}_${el.details.digitalAddressSource ?? ""}_${el.eventTimestamp}`;
        if (seen.has(key)) { return false; }
        seen.add(key);
        return true;
    });

    filtered.forEach((el) => {
        const ts = formatTimestamp(el.eventTimestamp);
        const label = TRADUZIONI_CATEGORIA[el.category] ?? el.category;
        const desc = getEventDescription(el);
        if (desc.fromDescrittore) {
            // if the description comes from a descriptor, we include ":" in the line"
            if (desc.descrizione) {
                lines.push(`* [${ts.date} ${ts.time}] ${label}: ${desc.descrizione}`);
            } else {
                lines.push(`* [${ts.date} ${ts.time}] ${label}`);
            }
        } else {
            lines.push(`* [${ts.date} ${ts.time}] ${label}`);
        }
    });

    return lines.join("\n");
}